import type { JsonValue } from "../nest/generated"
import { parseTaskAttention, type TaskAttention, type RepoRecord, type TaskRecord } from "../persistence/records"
import { taskIsSleeping } from "../store/taskAttention"
import { latestTaskActivityAt } from "../store/taskActivity"

export const ACTIVITY_SUMMARY_NAMESPACE = "wildlark.activity"
export const ACTIVITY_SUMMARY_KEY = "summary.v1"
const RECENT_TASK_LIMIT = 20

export interface ActivitySummaryTask {
    attention?: TaskAttention
    sleeping?: boolean
    lastActivityAt: string
    repoName: string
    running: boolean
    sessionIds: string[]
    taskId: string
    title: string
    unread: boolean
    workspaceId: string
}

export interface ComputerActivitySummary {
    generatedAt: string
    needsAttentionCount: number
    openTaskCount: number
    recentTasks: ActivitySummaryTask[]
    v: 1
    workspaceCount: number
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

export function buildComputerActivitySummary({
    repos,
    tasks,
    runningTaskIds,
    generatedAt = new Date().toISOString(),
}: {
    repos: RepoRecord[]
    tasks: TaskRecord[]
    runningTaskIds: ReadonlySet<string>
    generatedAt?: string
}): JsonValue {
    const now = Date.parse(generatedAt)
    const repoNames = new Map(repos.map((repo) => [repo.id, repo.name]))
    const openTasks = tasks
        .filter((task) => !task.closed)
        .map((task) => {
            const lastActivityAt = latestTaskActivityAt(task, now) ?? task.createdAt
            return {
                ...(task.attention ? { attention: { ...task.attention } } : {}),
                sleeping: taskIsSleeping(task, now),
                lastActivityAt,
                repoName: repoNames.get(task.repoId) ?? "Workspace",
                running: runningTaskIds.has(task.id),
                sessionIds: task.sessions.flatMap((session) => (session.nestSessionId ? [session.nestSessionId] : [])),
                taskId: task.id,
                title: task.title,
                unread: !taskIsSleeping(task, now) && (!task.lastViewedAt || lastActivityAt > task.lastViewedAt),
                workspaceId: task.workspaceId,
            }
        })
        .sort(
            (left, right) =>
                Number(left.sleeping) - Number(right.sleeping) ||
                Number(right.running) - Number(left.running) ||
                right.lastActivityAt.localeCompare(left.lastActivityAt)
        )
    const visibleTaskIds = new Set(
        openTasks
            .filter((task) => !task.running)
            .slice(0, RECENT_TASK_LIMIT)
            .map((task) => task.taskId)
    )
    for (const task of openTasks) {
        if (task.running) visibleTaskIds.add(task.taskId)
    }

    return {
        generatedAt,
        needsAttentionCount: openTasks.filter((task) => task.unread && !task.running).length,
        openTaskCount: openTasks.length,
        recentTasks: openTasks.filter((task) => visibleTaskIds.has(task.taskId)),
        v: 1,
        workspaceCount: repos.filter((repo) => !repo.archived).length,
    }
}

export function parseComputerActivitySummary(value: unknown): ComputerActivitySummary | null {
    if (!isRecord(value) || value.v !== 1 || typeof value.generatedAt !== "string") return null
    if (typeof value.needsAttentionCount !== "number" || typeof value.openTaskCount !== "number" || typeof value.workspaceCount !== "number") return null
    if (!Array.isArray(value.recentTasks)) return null

    const recentTasks: ActivitySummaryTask[] = []
    for (const task of value.recentTasks) {
        if (!isRecord(task)) return null
        if (
            typeof task.lastActivityAt !== "string" ||
            typeof task.repoName !== "string" ||
            typeof task.running !== "boolean" ||
            !Array.isArray(task.sessionIds) ||
            !task.sessionIds.every((sessionId) => typeof sessionId === "string") ||
            typeof task.taskId !== "string" ||
            typeof task.title !== "string" ||
            typeof task.unread !== "boolean" ||
            typeof task.workspaceId !== "string"
        ) {
            return null
        }
        const attention = parseTaskAttention(task.attention)
        recentTasks.push({
            ...(attention ? { attention } : {}),
            ...(typeof task.sleeping === "boolean" ? { sleeping: task.sleeping } : {}),
            lastActivityAt: task.lastActivityAt,
            repoName: task.repoName,
            running: task.running,
            sessionIds: task.sessionIds,
            taskId: task.taskId,
            title: task.title,
            unread: task.unread,
            workspaceId: task.workspaceId,
        })
    }

    return {
        generatedAt: value.generatedAt,
        needsAttentionCount: value.needsAttentionCount,
        openTaskCount: value.openTaskCount,
        recentTasks,
        v: 1,
        workspaceCount: value.workspaceCount,
    }
}
